import '~/components/trip-details-form/trip-details-form';
import { TripDetailsForm, TripDetailsEvent, TripDetails } from '~/components/trip-details-form/trip-details-form';
import '~/components/loader/loader';
import { LoadingIndicator } from '~/components/loader/loader';
import { Accommodation, AccommodationResult } from '~/types/API/AccommodationResult';
import '~/components/accommodation-card/accommodation-card';
import { AccommodationFeatures } from '~/components/accommodation-card/accommodation-card';

class DVGAccommodatieLijst {
  private wrapperElement: HTMLElement | null = document.querySelector('#dvg-accommodation-list');
  private listElement: HTMLUListElement | null = document.querySelector('#dvg-accommodation-list-results');
  private tripForm: TripDetailsForm | null = document.querySelector('#dvg-trip-details-form');
  private loadingIndicator: LoadingIndicator | null = document.querySelector('#dvg-accommodation-list__loading-indicator');

  private parkID: string | null = this.wrapperElement?.getAttribute('data-park-id') || null;
  private limit: number | null = this.wrapperElement?.getAttribute('data-limit') ? parseInt(this.wrapperElement.getAttribute('data-limit') as string) : null;

  amenityList = [
    { apiCode: '', iconCode: 'romantic' },
    { apiCode: 'PetsAllowed', iconCode: 'pets-allowed' },
    { apiCode: 'ForestNearby', iconCode: 'forest' },
    { apiCode: 'Wifi', iconCode: 'wifi' },
    { apiCode: 'CoffeeApparatus', iconCode: 'free-coffee' },
  ];

  uspList = [
    { apiCode: 'Wifi', displayText: 'Wifi' },
    { apiCode: 'CoffeeApparatus', displayText: 'Gratis koffie' },
    { apiCode: 'NespressoEquipment', displayText: 'Nespresso apparaat' },
    { apiCode: 'Parkingplace', displayText: 'Gratis parkeren' },
    { apiCode: 'BreakfastIncluded', displayText: 'Ontbijt inbegrepen' },
    { apiCode: 'PetsAllowed', displayText: 'Huisdieren welkom' },
  ];

  constructor() {
    this.initEventListeners();
  }

  private initEventListeners() {
    if (this.tripForm) {
      this.tripForm.addEventListener('dvg:trip-details-change', this.onTripDetailsChange);
    }
  }

  private onTripDetailsChange = (event: TripDetailsEvent) => {
    if (!this.listElement || !this.tripForm) return;

    this.clearAccommodationList();
    this.fetchAccommodations(event.detail);
  };

  clearAccommodationList() {
    if (this.listElement) {
      this.listElement.replaceChildren();
    }
  }

  private async fetchAccommodations(details: TripDetails) {
    if (!this.listElement || !this.tripForm) return;

    this.showLoadingState();

    const apiUrl = this.constructAPIUrl(details);
    console.log('Fetching accommodations from API URL:', apiUrl);

    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Error details:', errorData);
        throw new Error(`API request failed: ${ response.status }`);
      }

      const { results } = await response.json() as AccommodationResult;
      this.renderResults(results.data);
    } catch (error) {
      console.error('Error fetching accommodations:', error);
    }
    this.hideLoadingState();
  }

  private renderResults(accommodations: Accommodation[]) {
    if (!this.listElement) return;

    accommodations.forEach((accommodation) => {
      const listItem = this.createAccommodationItem(accommodation);
      this.listElement?.appendChild(listItem);
    });
  }

  createAccommodationItem(accommodation: Accommodation): HTMLLIElement {
    const li = document.createElement('li');
    li.className = 'search-result-item';

    const occupancy = accommodation.max_occupancy;

    let properties: any[] = [];
    if (accommodation.properties) {
      properties = accommodation.properties;
    }
    const noOfBedrooms = parseInt(properties.find(prop => prop.code === 'CountBedrooms')?.quantity || '0')

    const features: AccommodationFeatures = {
      occupancy,
      bedrooms: noOfBedrooms,
      hotTub: properties.some(prop => prop.code === 'Jacuzzi' && prop.bool_value),
      petsAllowed: properties.some(prop => prop.code === 'PetsAllowed' && prop.bool_value),
    };

    li.innerHTML = `
      <dvg-accommodation-card
        name="${accommodation.name}"
        location="${accommodation.city}, ${accommodation.region}, ${accommodation.country}"
        image-url="${accommodation.images[0]?.url || ''}"
        price="${accommodation.minimum_price}"
        slug="/vakantiehuisjes/${accommodation.slug}"
        orientation="vertical"
      ></dvg-accommodation-card>
    `;

    const card = li.querySelector('dvg-accommodation-card');
    if (card) {
      card.features = features;
    }

    return li;
  }

  private constructAPIUrl(details: TripDetails): string {
    const baseUrl = `${window.DVGBookingConfig.apiBaseUrl}/zoeken`;
    const url = new URL(baseUrl, window.location.origin);

    if (this.parkID) {
      url.searchParams.append('parks', this.parkID);
    }
    if (this.limit) {
      url.searchParams.append('limit', this.limit.toString());
    }
    if (details.startDate) {
      url.searchParams.append('arrival', details.startDate);
    }
    if (details.endDate) {
      url.searchParams.append('departure', details.endDate);
    }
    if (details.party) {
      if (details.party.adults) {
        url.searchParams.append('adults', details.party.adults.toString());
      }
      if (details.party.children) {
        url.searchParams.append('children', details.party.children.toString());
      }
      if (details.party.infants) {
        url.searchParams.append('infants', details.party.infants.toString());
      }
      if (details.party.pets) {
        url.searchParams.append('pets', details.party.pets.toString());
      }
    }

    return url.toString();
  }

  private showLoadingState() {
    if (!this.loadingIndicator || !this.tripForm) return;

    this.loadingIndicator.show = true;
    this.tripForm.loading = true;
  }

  private hideLoadingState() {
    if (!this.loadingIndicator || !this.tripForm) return;

    this.loadingIndicator.show = false;
    this.tripForm.loading = false;
  }
}

window.addEventListener('DOMContentLoaded', () => {
  new DVGAccommodatieLijst();
});
